import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Radar } from "lucide-react";
import { adminApi, apiError, MONTHS, STAGE_LABELS } from "../adminApi";
import { Field, Panel, Pill, ScoreBar, buttonClass, inputClass } from "../components/Bits";

const ACTIVITY_TYPES = ["note", "call", "email", "meeting"];

const PEO_STATUS_COPY = {
  confirmed: { label: "Confirmed", tone: "gold" },
  likely: { label: "Likely on a PEO", tone: "sage" },
  peo_entity: { label: "This record is a PEO", tone: "terra" },
  unknown: { label: "No signal", tone: "neutral" },
};

const money = (value) =>
  value == null ? "—" : `$${Math.round(value).toLocaleString()}`;

const CarrierPanel = ({ account }) => {
  const summary = account.carrier_summary;
  if (!summary) {
    return (
      <p className="text-sm leading-relaxed text-cream/45" data-testid="carriers-empty">
        No Schedule A on file for this filing — either the plan is self-funded with no insured
        contracts, or the carrier data has not been loaded yet.
      </p>
    );
  }
  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-3">
        <Field label="Incumbent carrier" value={summary.incumbent_carrier} testid="field-carrier" />
        <Field label="Incumbent broker" value={summary.incumbent_broker} testid="field-broker" />
        <Field label="Coverage ends" value={summary.latest_policy_end} />
        <Field label="Premium (all contracts)" value={money(summary.total_premium)} />
        <Field label="Broker commission"
               value={summary.commission_suspect
                 ? `${money(summary.total_commission)} — filed figure looks wrong`
                 : money(summary.total_commission)} testid="field-commission" />
        <Field label="Loss ratio"
               value={summary.loss_ratio ? `${Math.round(summary.loss_ratio * 100)}%` : null} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-left">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
              <th className="py-2 pr-3">Carrier</th>
              <th className="py-2 pr-3">Coverage</th>
              <th className="py-2 pr-3">Covered</th>
              <th className="py-2 pr-3">Premium</th>
              <th className="py-2">Commission</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/10">
            {(account.sched_a || []).map((contract, index) => (
              <tr key={`${contract.contract_number}-${index}`} data-testid={`contract-row-${index}`}>
                <td className="py-2.5 pr-3 text-xs text-cream/85">
                  {contract.carrier}
                  {contract.contract_number ? (
                    <span className="block text-[10px] text-cream/35">#{contract.contract_number}</span>
                  ) : null}
                </td>
                <td className="py-2.5 pr-3 text-xs text-cream/55">
                  {(contract.coverage || []).join(", ") || "—"}
                </td>
                <td className="py-2.5 pr-3 font-mono text-xs text-cream/70">{contract.covered ?? "—"}</td>
                <td className="py-2.5 pr-3 font-mono text-xs text-cream/70">{money(contract.premium)}</td>
                <td className="py-2.5 font-mono text-xs text-cream/70">{money(contract.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(account.brokers || []).length > 1 ? (
        <p className="text-xs leading-relaxed text-cream/40">
          Also paid:{" "}
          {account.brokers.slice(1).map((b) => `${b.name} (${money(b.commission)})`).join(", ")}
        </p>
      ) : null}
    </div>
  );
};

const PeoFitPanel = ({ accountId }) => {
  const [fit, setFit] = useState(null);

  useEffect(() => {
    adminApi.get(`/crm/accounts/${accountId}/peo-fit`)
      .then(({ data }) => setFit(data)).catch(() => {});
  }, [accountId]);

  if (!fit) return <p className="text-sm text-cream/40">Working out the best-fit partner…</p>;
  const { fit: pick, partner, review_window: review } = fit;

  return (
    <div className="space-y-4" data-testid="peo-fit-body">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
          {pick.matched ? pick.profile : "No matrix row fired"}
        </p>
        <p className="mt-2 font-serif text-2xl leading-none text-cream"
           data-testid="peo-fit-first-choice">
          {pick.first_choice_name}
          {partner ? (
            <span className="ml-3 font-mono text-xs tracking-[0.2em] text-gold">
              {partner.overall}/100
            </span>
          ) : null}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-cream/60">{pick.why}</p>
      </div>

      {pick.reasons?.length ? (
        <ul className="space-y-1" data-testid="peo-fit-reasons">
          {pick.reasons.map((reason) => (
            <li key={reason} className="text-xs leading-relaxed text-cream/40">· {reason}</li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/25">backup</span>
        <Pill tone="neutral">{pick.backup_name || "—"}</Pill>
        {pick.alternates?.map((row) => (
          <Pill key={row.rule_id} tone="sage">also fits {row.first_choice_name}</Pill>
        ))}
      </div>

      {review ? (
        <p className="text-xs leading-relaxed text-cream/45" data-testid="peo-fit-review-window">
          Renewal {review.renewal} — open the review by {review.start_review}
          {review.days_until_start >= 0
            ? ` (${review.days_until_start} days away)`
            : " (that window is already open)"}. {review.guidance}.
        </p>
      ) : (
        <p className="text-xs leading-relaxed text-cream/35" data-testid="peo-fit-no-renewal">
          No renewal month on file — ask for it on the first call; it sets the whole review window.
        </p>
      )}
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cream/25">
        Wave 1 scorecard, {fit.as_of}
      </p>
    </div>
  );
};

const PeoSignal = ({ account, onConfirm }) => {
  const [name, setName] = useState("");
  const signal = account.peo_signal || { status: account.peo_status || "unknown" };
  const copy = PEO_STATUS_COPY[signal.status] || PEO_STATUS_COPY.unknown;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Pill tone={copy.tone}>{copy.label}</Pill>
        {account.current_peo ? (
          <span className="text-sm text-cream" data-testid="peo-name">{account.current_peo}</span>
        ) : null}
        {signal.confidence ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
            {signal.confidence}% confidence
          </span>
        ) : null}
      </div>

      {signal.method ? (
        <p className="text-sm leading-relaxed text-cream/60" data-testid="peo-method">{signal.method}</p>
      ) : null}

      {(signal.evidence || []).length ? (
        <ul className="space-y-2">
          {signal.evidence.map((line) => (
            <li key={line} className="flex gap-2 text-xs leading-relaxed text-cream/70">
              <span className="text-gold">·</span>
              {line}
            </li>
          ))}
        </ul>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) {
            onConfirm(name.trim());
            setName("");
          }
        }}        className="flex flex-wrap gap-2 border-t border-cream/10 pt-4"
        data-testid="peo-confirm-form"
      >
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder={account.current_peo ? "Correct the PEO name" : "Name the PEO once you know it"}
               className={`${inputClass} max-w-xs`} data-testid="peo-name-input" />
        <button type="submit" className={buttonClass} data-testid="peo-confirm-submit">
          Confirm PEO
        </button>
        {account.current_peo ? (
          <button type="button" onClick={() => onConfirm("")} data-testid="peo-clear-button"
                  className="text-xs text-cream/40 transition-colors duration-200 hover:text-terra">
            Clear
          </button>
        ) : null}
      </form>
    </div>
  );
};

const EnrolPanel = ({ accountId, onDone }) => {
  const [cadences, setCadences] = useState([]);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/crm/sequences")
      .then(({ data }) => setCadences(data.sequences)).catch(() => {});
  }, []);

  const enrol = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/crm/sequences/${choice}/enroll`,
                                           { account_ids: [accountId] });
      if (data.enrolled) {
        toast.success("Enrolled — the first touch is in today's queue");
      } else {
        toast.error(data.skipped[0] || "Nothing to enrol");
      }
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="account-enrol-panel">
      <select value={choice} onChange={(e) => setChoice(e.target.value)} className={inputClass}
              data-testid="account-enrol-select">
        <option value="">Choose a cadence</option>
        {cadences.map((cadence) => (
          <option key={cadence.id} value={cadence.id}>{cadence.name}</option>
        ))}
      </select>
      <button type="button" onClick={enrol} disabled={busy || !choice} className={buttonClass}
              data-testid="account-enrol-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Add to cadence
      </button>
      <p className="text-xs leading-relaxed text-cream/35">
        One account, straight into the cadence. Each touch appears on the Sequences queue with this
        account's own filing facts filled in.
      </p>
    </div>
  );
};

export default function AdminAccountDetail() {
  const { accountId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [activity, setActivity] = useState({ type: "note", body: "", outcome: "" });
  const [contact, setContact] = useState({ first_name: "", last_name: "", title: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await adminApi.get(`/crm/accounts/${accountId}`);
      setData(d);
    } catch (e) {
      setError(apiError(e));
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const updateAccount = async (changes) => {
    try {
      await adminApi.patch(`/crm/accounts/${accountId}`, changes);
      toast.success("Account updated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitActivity = async (event) => {
    event.preventDefault();
    if (!activity.body.trim()) return;
    setBusy(true);
    try {
      await adminApi.post("/crm/activities", { account_id: accountId, ...activity });
      setActivity({ type: activity.type, body: "", outcome: "" });
      toast.success("Logged");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitContact = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = { account_id: accountId, ...contact };
      Object.keys(payload).forEach((key) => payload[key] === "" && delete payload[key]);
      await adminApi.post("/crm/contacts", payload);
      setContact({ first_name: "", last_name: "", title: "", email: "", phone: "" });
      toast.success("Contact added");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <p className="text-sm text-terra" data-testid="account-detail-error">{error}</p>;
  if (!data) return <p className="text-sm text-cream/40" data-testid="account-detail-loading">Loading…</p>;

  const { account, contacts, activities, tasks } = data;

  return (
    <div className="space-y-6" data-testid="admin-account-detail">
      <Link to="/admin/accounts" data-testid="back-to-accounts"
            className="inline-flex items-center gap-2 text-xs text-cream/50 transition-colors duration-200 hover:text-cream">
        <ArrowLeft className="h-3.5 w-3.5" /> All accounts
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-5">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terra">
            {account.segment} · {account.source === "form5500" ? "Form 5500" : "Manual entry"}
          </p>
          <h1 className="mt-3 font-serif text-4xl font-light leading-tight text-cream"
              data-testid="account-name">
            {account.name}
          </h1>
          <p className="mt-2 text-sm text-cream/50">
            {[account.city, account.state, account.industry].filter(Boolean).join(" · ")}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {account.fully_insured ? <Pill tone="gold">Fully insured</Pill> : null}
            {account.self_funded ? <Pill tone="sage">Self-funded assets</Pill> : null}
            {account.do_not_contact ? <Pill tone="terra">Do not contact</Pill> : null}
            {(account.benefits || []).slice(0, 5).map((benefit) => (
              <Pill key={benefit}>{benefit}</Pill>
            ))}
          </div>
        </div>

        <div className="flex flex-col items-end gap-3">
          <ScoreBar score={account.score || 0} />
          <select value={account.stage || "new"} data-testid="account-stage-select"
                  onChange={(e) => updateAccount({ stage: e.target.value })}
                  className={`${inputClass} w-44`}>
            {Object.entries(STAGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button type="button" data-testid="account-dnc-button"
                  onClick={() => updateAccount({ do_not_contact: !account.do_not_contact })}
                  className="text-xs text-cream/45 transition-colors duration-200 hover:text-terra">
            {account.do_not_contact ? "Clear do-not-contact" : "Mark do-not-contact"}
          </button>
          <Link
            to={`/admin/co-employer?company=${encodeURIComponent(account.name)}&state=${account.state || "TX"}&run=1`}
            data-testid="account-run-radar"
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs text-gold transition-colors duration-200 hover:bg-gold/10"
          >
            <Radar className="h-3.5 w-3.5" /> Run radar
          </Link>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Panel title="Filing facts" testid="panel-filing-facts">
            <div className="grid gap-5 sm:grid-cols-3">
              <Field label="Covered lives" value={account.participants} testid="field-participants" />
              <Field label="Lives at plan-year start" value={account.participants_boy} />
              <Field label="Renewal month"
                     value={account.renewal_month ? MONTHS[account.renewal_month - 1] : null} />
              <Field label="EIN" value={account.ein} />
              <Field label="Plan number" value={account.plan_number} />
              <Field label="Plan year begin" value={account.plan_year_begin} />
              <Field label="Sponsor phone" value={account.phone} testid="field-phone" />
              <Field label="Address"
                     value={[account.address, account.city, account.zip].filter(Boolean).join(", ")} />
              <Field label="Filing received" value={account.filing_received} />
              <Field label="Plan name" value={account.plan_name} />
              <Field label="Plan administrator" value={account.plan_administrator} />
              <Field label="Current PEO" value={account.current_peo} />
            </div>
          </Panel>

          <Panel title="Coverage & incumbents" testid="panel-carriers">
            <CarrierPanel account={account} />
          </Panel>

          <Panel title="PEO signal" testid="panel-peo-signal">
            <PeoSignal account={account} onConfirm={(name) => updateAccount({ current_peo: name })} />
          </Panel>

          <Panel title="Partner to lead with" testid="panel-peo-fit">
            <PeoFitPanel accountId={accountId} />
          </Panel>

          <Panel title="Why this scored where it did" testid="panel-score-factors">
            {(account.score_factors || []).length === 0 ? (
              <p className="text-sm text-cream/40">No scoring factors recorded.</p>
            ) : (
              <ul className="space-y-3">
                {account.score_factors.map((factor) => (
                  <li key={factor.factor} className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm text-cream/85">{factor.note}</p>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                        {factor.factor.replace(/_/g, " ")}
                      </p>
                    </div>
                    <span className="font-mono text-xs text-gold">+{factor.points}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Activity" testid="panel-activity-log">
            <form onSubmit={submitActivity} className="space-y-3" data-testid="activity-form">
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_TYPES.map((type) => (
                  <button key={type} type="button" onClick={() => setActivity({ ...activity, type })}
                          data-testid={`activity-type-${type}`}
                          className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors duration-200 ${
                            activity.type === type
                              ? "border-gold/60 text-gold"
                              : "border-cream/15 text-cream/50 hover:text-cream"
                          }`}>
                    {type}
                  </button>
                ))}
              </div>
              <textarea rows={3} value={activity.body} data-testid="activity-body-input"
                        onChange={(e) => setActivity({ ...activity, body: e.target.value })}
                        placeholder="What happened? Keep it factual — this is the record."
                        className={`${inputClass} resize-none`} />
              <div className="flex flex-wrap items-center gap-3">
                <input value={activity.outcome} data-testid="activity-outcome-input"
                       onChange={(e) => setActivity({ ...activity, outcome: e.target.value })}
                       placeholder="Outcome (connected, voicemail, no answer…)"
                       className={`${inputClass} max-w-xs`} />
                <button type="submit" disabled={busy} className={buttonClass} data-testid="activity-submit">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Log it
                </button>
              </div>
            </form>

            <ul className="mt-6 divide-y divide-cream/10">
              {activities.map((item) => (
                <li key={item.id} className="py-3" data-testid={`activity-${item.id}`}>
                  <p className="text-sm text-cream/85">{item.body}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                    {item.type}
                    {item.meta?.outcome ? ` · ${item.meta.outcome}` : ""} · {item.user_name} ·{" "}
                    {new Date(item.created_at).toLocaleString()}
                  </p>
                </li>
              ))}
              {activities.length === 0 ? (
                <li className="py-3 text-sm text-cream/40">Nothing logged yet.</li>
              ) : null}
            </ul>
          </Panel>
        </div>

        <div className="space-y-5">
          <Panel title="Contacts" testid="panel-contacts">
            {(account.filing_contacts || []).length ? (
              <div className="mb-4 rounded-lg border border-cream/10 bg-cream/5 p-3"
                   data-testid="filing-signers">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                  On the filing
                </p>
                <p className="mt-1.5 text-xs leading-relaxed text-cream/70">
                  {account.filing_contacts.map((person) =>
                    `${person.first_name} ${person.last_name} (${person.title})`).join(", ")}
                </p>
                <button type="button" data-testid="contact-from-filing"
                        onClick={async () => {
                          try {
                            const { data } = await adminApi.post(
                              `/crm/accounts/${accountId}/contacts/from-filing`);
                            toast.success(data.added.length
                              ? `Added ${data.added.join(", ")}`
                              : "Already on the account");
                            load();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                        className="mt-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-200 hover:text-cream">
                  Add as a contact
                </button>
              </div>
            ) : null}
            <ul className="divide-y divide-cream/10">
              {contacts.map((person) => (
                <li key={person.id} className="py-3" data-testid={`contact-${person.id}`}>
                  <p className="text-sm text-cream">
                    {person.first_name} {person.last_name}
                    {person.is_primary ? <span className="ml-2 text-[10px] text-gold">PRIMARY</span> : null}
                    {person.opted_out ? (
                      <span className="ml-2 text-[10px] text-terra"
                            data-testid={`contact-unsubscribed-${person.id}`}>UNSUBSCRIBED</span>
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs text-cream/45">{person.title || "—"}</p>
                  <p className="mt-1 text-xs text-cream/60">
                    {[person.email, person.phone].filter(Boolean).join(" · ") || "No contact details"}
                  </p>
                </li>
              ))}
              {contacts.length === 0 ? (
                <li className="py-3 text-sm text-cream/40">No contacts yet.</li>
              ) : null}
            </ul>

            {contacts.length > 0 && !contacts.some((person) => person.email) ? (
              <button type="button" data-testid="account-find-email"
                      onClick={async () => {
                        try {
                          const { data } = await adminApi.post(
                            `/crm/accounts/${accountId}/find-email`);
                          if (data.matched && data.email) {
                            toast.success(`Found ${data.email}`);
                          } else {
                            toast.error(data.note || "No email found for this contact");
                          }
                          load();
                        } catch (e) {
                          toast.error(apiError(e));
                        }
                      }}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs text-gold transition-colors duration-200 hover:bg-gold/10">
                Find the work email
              </button>
            ) : null}

            <form onSubmit={submitContact} className="mt-5 space-y-2" data-testid="contact-form">
              <div className="flex gap-2">
                <input required value={contact.first_name} placeholder="First name"
                       data-testid="contact-first-input" className={inputClass}
                       onChange={(e) => setContact({ ...contact, first_name: e.target.value })} />
                <input value={contact.last_name} placeholder="Last name" className={inputClass}
                       data-testid="contact-last-input"
                       onChange={(e) => setContact({ ...contact, last_name: e.target.value })} />
              </div>
              <input value={contact.title} placeholder="Title" className={inputClass}
                     data-testid="contact-title-input"
                     onChange={(e) => setContact({ ...contact, title: e.target.value })} />
              <input type="email" value={contact.email} placeholder="Email" className={inputClass}
                     data-testid="contact-email-input"
                     onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              <input value={contact.phone} placeholder="Phone" className={inputClass}
                     data-testid="contact-phone-input"
                     onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
              <button type="submit" disabled={busy} className={buttonClass} data-testid="contact-submit">
                Add contact
              </button>
            </form>
          </Panel>

          <Panel title="Sequence" testid="panel-account-sequence">
            <EnrolPanel accountId={accountId} onDone={load} />
          </Panel>

          <Panel title="Tasks" testid="panel-tasks">
            <ul className="divide-y divide-cream/10">
              {tasks.map((task) => (
                <li key={task.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm text-cream/85">{task.title}</p>
                    <p className="mt-0.5 text-xs text-cream/40">{task.due_date || "No due date"}</p>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/40">
                    {task.status}
                  </span>
                </li>
              ))}
              {tasks.length === 0 ? (
                <li className="py-3 text-sm text-cream/40">No tasks on this account.</li>
              ) : null}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
